// src/components/TaproomKegCount.tsx
// Taproom Keg Count: confirm cooler inventory and catch problems.
// Approve records the lists only — inventory moves only through orders/returns.
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import '../App.css';
import { apiFetch } from '../utils/api';
import { API_BASE_URL } from '../config';
import { isValidKegCode, normalizeKegCode } from '../utils/kegCode';
import { SCAN_COOLDOWN_MS } from '../utils/kegScanFilter';
import KegScanOverlay, { KegScanAccepted } from './KegScanOverlay';
import { primeScanAudio } from '../utils/scanFeedback';
import { TAPROOMS, formatCentral } from './KegReturns';

export interface CountLine {
  id: number;
  kegCode: string;
  kegId?: number | null;
  flag?: 'unknown' | 'elsewhere' | 'returned_empty' | string | null;
  wasAt?: string | null;
  effectiveProductId?: number | null;
  effectiveProductName?: string | null;
  currentProductName?: string | null;
  currentStatus?: string | null;
  overrideProductId?: number | null;
  packageType?: string | null;
  needsProduct?: boolean;
  returnedEmptyChoice?: 'leave_out' | 'restore_full' | null;
  returnWarning?: string | null;
  restoreProductName?: string | null;
  restoreProductId?: number | null;
  excludedFromTally?: boolean;
  atBreweryCooler?: boolean;
  suggestedOrderDate?: string | null;
  itemName?: string | null;
}

export interface TallyRow {
  itemName: string;
  productName?: string | null;
  packageType?: string | null;
  scanned: number;
  onHand: number;
  diff: number;
}

export interface MissingKeg {
  kegId: number;
  kegCode: string;
  status?: string | null;
  productName?: string | null;
  packageType?: string | null;
  lastScanned?: string | null;
  arrivedDate?: string | null;
  arrivedVia?: string | null;
  orderRef?: string | null;
  arrivalNote?: string | null;
  lastHistoryAction?: string | null;
  lastHistoryDate?: string | null;
}

export interface ElsewhereKeg {
  kegCode: string;
  kegId?: number | null;
  wasAt?: string | null;
  status?: string | null;
  productName?: string | null;
  packageType?: string | null;
  itemName?: string | null;
  atBreweryCooler?: boolean;
  suggestedOrderDate?: string | null;
}

export interface ReturnedEmptyKeg {
  kegCode: string;
  productName?: string | null;
  packageType?: string | null;
  wasAt?: string | null;
  returnWarning?: string | null;
  returnDate?: string | null;
  returnSource?: string | null;
}

export interface TaproomKegCountDoc {
  id: number;
  ref: string;
  status: 'draft' | 'approved' | string;
  taproom: string;
  notes?: string | null;
  createdAt?: string;
  createdByEmail?: string | null;
  approvedAt?: string | null;
  approvedByEmail?: string | null;
  scannedCount?: number | null;
  lines: CountLine[];
  tally: TallyRow[];
  totals: {
    scanned?: number;
    matched?: number;
    counted?: number;
    onHand?: number;
    diffAbs?: number;
    net?: number;
    returnedEmpty?: number;
    elsewhere?: number;
    missing?: number;
    unknown?: number;
  };
  notScanned: MissingKeg[];
  missing?: MissingKeg[];
  elsewhere: ElsewhereKeg[];
  returnedEmpty?: ReturnedEmptyKeg[];
  unknown?: string[];
  blockers: string[];
  alreadyApproved?: boolean;
}

interface Notice {
  level: 'ok' | 'warn' | 'info' | 'error' | string;
  message: string;
}

const COUNTS_API = `${API_BASE_URL}/api/taproom-keg-counts`;

function sizeLabel(packageType?: string | null) {
  return String(packageType || 'No size').replace(/^Keg\s*-\s*/i, '');
}

function signed(n: number) {
  if (!n) return '0';
  return n > 0 ? `+${n}` : String(n);
}

function siteFor(taproom: string) {
  return TAPROOMS.find((t) => t.name === taproom)?.site || '';
}

function formatDay(d?: string | null) {
  if (!d) return null;
  const day = String(d).slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(day)) return String(d);
  const dt = new Date(`${day}T12:00:00`);
  if (Number.isNaN(dt.getTime())) return day;
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
}

async function readJson(res: Response) {
  return res.json().catch(() => ({}));
}

/** Running tally of matched kegs by product + keg size. */
export function tallyFromLines(lines: CountLine[]) {
  const map = new Map<string, { label: string; size: string; count: number }>();
  for (const l of lines) {
    if (l.flag === 'unknown' || l.flag === 'elsewhere' || l.flag === 'returned_empty') continue;
    if (l.excludedFromTally) continue;
    const label = l.effectiveProductName || 'No product';
    const size = sizeLabel(l.packageType);
    const key = `${label}\0${size}`;
    const row = map.get(key) || { label, size, count: 0 };
    row.count += 1;
    map.set(key, row);
  }
  return Array.from(map.values()).sort((a, b) => a.label.localeCompare(b.label) || a.size.localeCompare(b.size));
}

/**
 * Height of the fixed phone bottom nav (.navbar-mobile) when it is showing.
 */
export function useBottomNavClearance(): number | null {
  const [px, setPx] = useState<number | null>(null);
  useEffect(() => {
    if (typeof window === 'undefined') return undefined;
    const measure = () => {
      const nav = document.querySelector('.navbar-mobile') as HTMLElement | null;
      if (!nav || window.getComputedStyle(nav).display === 'none') {
        setPx(null);
        return;
      }
      const rect = nav.getBoundingClientRect();
      setPx(rect.height > 0 ? Math.ceil(window.innerHeight - rect.top) : null);
    };
    measure();
    window.addEventListener('resize', measure);
    const RO = (window as any).ResizeObserver;
    const nav = document.querySelector('.navbar-mobile');
    const ro = RO && nav ? new RO(measure) : null;
    if (ro && nav) ro.observe(nav);
    return () => {
      window.removeEventListener('resize', measure);
      if (ro) ro.disconnect();
    };
  }, []);
  return px;
}

const NoticeBar: React.FC<{ notice: Notice | null }> = ({ notice }) =>
  notice ? (
    <div
      className={`tkc-notice tkc-notice--${notice.level}`}
      role={notice.level === 'error' ? 'alert' : 'status'}
      data-testid="tkc-notice"
      data-level={notice.level}
    >
      {notice.message}
    </div>
  ) : null;

const TallyChips: React.FC<{ lines: CountLine[] }> = ({ lines }) => {
  const rows = tallyFromLines(lines);
  const total = rows.reduce((n, r) => n + r.count, 0);
  return (
    <div className="tkc-tally" data-testid="tkc-tally">
      {rows.map((r) => (
        <span key={`${r.label}-${r.size}`} className="tkc-chip" data-testid="tkc-tally-chip">
          <strong>{r.count}</strong> {r.label} <em>{r.size}</em>
        </span>
      ))}
      <span className="tkc-chip tkc-chip--total" data-testid="tkc-total">
        {total} matched
      </span>
    </div>
  );
};

type CountListRow = {
  id: number;
  ref: string;
  status: string;
  taproom: string;
  createdAt?: string;
  createdByEmail?: string | null;
  approvedAt?: string | null;
  approvedByEmail?: string | null;
  scannedCount?: number | null;
};

function resumeLabel(c: { id: number; scannedCount?: number | null }) {
  return `Resume count #${c.id} (${c.scannedCount ?? 0} kegs scanned)`;
}

/** Start a count + open drafts + count history. */
export const TaproomKegCountList: React.FC = () => {
  const navigate = useNavigate();
  const [taproom, setTaproom] = useState('');
  const [rows, setRows] = useState<CountListRow[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const [resumePrompt, setResumePrompt] = useState<CountListRow | null>(null);

  useEffect(() => {
    apiFetch(COUNTS_API, { headers: { Accept: 'application/json' } })
      .then(async (r) => {
        if (!r.ok) throw new Error(`Failed to load counts: HTTP ${r.status}`);
        setRows(await r.json());
      })
      .catch((e: Error) => setError(e.message));
  }, []);

  const drafts = rows.filter((c) => c.status === 'draft');
  const history = rows.filter((c) => c.status !== 'draft');

  const createCount = async (forceNew = false) => {
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch(COUNTS_API, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ taproom, forceNew: forceNew || undefined }),
        allowHttpError: true,
      });
      const body = await readJson(res);
      if (res.status === 409 && body.code === 'DRAFT_EXISTS' && body.id != null) {
        setResumePrompt({
          id: Number(body.id),
          ref: body.ref || `TKC-${body.id}`,
          status: 'draft',
          taproom: body.taproom || taproom,
          createdAt: body.createdAt,
          createdByEmail: body.createdByEmail,
          scannedCount: body.scannedCount ?? 0,
        });
        return;
      }
      if (!res.ok) {
        setError(body.error || `Could not start the count (${res.status})`);
        return;
      }
      setResumePrompt(null);
      navigate(`/taproom-keg-counts/${body.id}`);
    } finally {
      setBusy(false);
    }
  };

  const start = async () => {
    if (!taproom) {
      setError('Pick the taproom you are counting');
      return;
    }
    setError(null);
    const existing = drafts.find((c) => c.taproom === taproom) || null;
    if (existing) {
      setResumePrompt(existing);
      return;
    }
    await createCount(false);
  };

  return (
    <div className="page-container tkc-page" data-testid="tkc-list">
      <h2 className="app-header" style={{ color: '#EEC930', fontSize: 24 }}>
        Taproom Keg Count
      </h2>
      <p>
        <Link to="/keg-tracking">← Keg Tracking</Link>
      </p>
      <p className="keg-return-hint">
        Walk the cooler and taps and scan every keg that is physically there. Approving records the count
        and its problem lists — it does not move inventory. Inventory only moves through taproom orders and
        keg returns. Scans save automatically; you can leave a draft and resume later.
      </p>
      {error && <div className="error">{error}</div>}
      <div className="tkc-start">
        <select
          value={taproom}
          onChange={(e) => {
            setTaproom(e.target.value);
            setResumePrompt(null);
          }}
          data-testid="tkc-taproom"
          aria-label="Taproom"
        >
          <option value="">— Select taproom —</option>
          {TAPROOMS.map((t) => (
            <option key={t.name} value={t.name}>
              {t.name} ({t.site})
            </option>
          ))}
        </select>
        <button
          type="button"
          className="tkc-btn tkc-btn--go"
          data-testid="tkc-start"
          disabled={busy}
          onClick={() => void start()}
        >
          Start Count
        </button>
      </div>

      {resumePrompt && (
        <div className="tkc-resume-prompt" data-testid="tkc-resume-prompt" role="status">
          <p>
            {resumePrompt.taproom} already has open draft <strong>{resumePrompt.ref}</strong> (
            {resumePrompt.scannedCount ?? 0} kegs scanned).
          </p>
          <div className="tkc-resume-prompt__actions">
            <button
              type="button"
              className="tkc-btn tkc-btn--go"
              data-testid="tkc-resume-existing"
              disabled={busy}
              onClick={() => navigate(`/taproom-keg-counts/${resumePrompt.id}`)}
            >
              {resumeLabel(resumePrompt)}
            </button>
            <button
              type="button"
              className="tkc-btn tkc-btn--muted"
              data-testid="tkc-force-new"
              disabled={busy}
              onClick={() => void createCount(true)}
            >
              Start a new count anyway
            </button>
          </div>
        </div>
      )}

      {drafts.length > 0 && (
        <>
          <h3 className="tkc-h3">Open drafts</h3>
          <div className="tkc-history" data-testid="tkc-drafts">
            {drafts.map((c) => (
              <div key={c.id} className="tkc-history-row tkc-draft-row" data-testid={`tkc-draft-${c.id}`}>
                <div className="tkc-history-main">
                  <strong>{c.ref}</strong> · {c.taproom}{' '}
                  <span className="keg-return-status keg-return-status-draft" data-testid="tkc-draft-badge">
                    Draft
                  </span>
                </div>
                <div className="tkc-history-meta">
                  Started {formatCentral(c.createdAt)} · {c.createdByEmail || '—'}
                </div>
                <div className="tkc-history-meta">{c.scannedCount ?? 0} kegs scanned</div>
                <button
                  type="button"
                  className="tkc-btn tkc-btn--go tkc-resume-btn"
                  data-testid={`tkc-resume-${c.id}`}
                  onClick={() => navigate(`/taproom-keg-counts/${c.id}`)}
                >
                  Resume
                </button>
              </div>
            ))}
          </div>
        </>
      )}

      <h3 className="tkc-h3">Count history</h3>
      {history.length === 0 ? (
        <div className="alert-info">{drafts.length ? 'No approved counts yet.' : 'No taproom keg counts yet.'}</div>
      ) : (
        <div className="tkc-history" data-testid="tkc-history">
          {history.map((c) => (
            <button
              type="button"
              key={c.id}
              className="tkc-history-row"
              data-testid={`tkc-history-${c.id}`}
              onClick={() => navigate(`/taproom-keg-counts/${c.id}`)}
            >
              <span className="tkc-history-main">
                <strong>{c.ref}</strong> · {c.taproom}{' '}
                <span className={`keg-return-status keg-return-status-${c.status}`}>{c.status}</span>
              </span>
              <span className="tkc-history-meta">
                {`${formatCentral(c.approvedAt)} · ${c.approvedByEmail || '—'}`}
              </span>
              <span className="tkc-history-meta">{c.scannedCount ?? 0} kegs scanned</span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};

/** Place-order panel for out-of-place (missed order) kegs. */
const PlaceOrderPanel: React.FC<{
  countId: number;
  kegs: ElsewhereKeg[];
  busy: boolean;
  onDone: (count: TaproomKegCountDoc, message: string) => void;
  onError: (msg: string) => void;
}> = ({ countId, kegs, busy, onDone, onError }) => {
  const [selected, setSelected] = useState<Set<string>>(() => new Set(kegs.map((k) => k.kegCode)));
  const [orderDate, setOrderDate] = useState(() => {
    const dates = kegs.map((k) => k.suggestedOrderDate).filter(Boolean).sort() as string[];
    return dates[0] || new Date().toLocaleDateString('en-CA', { timeZone: 'America/Chicago' });
  });
  const [warnings, setWarnings] = useState<string[]>([]);
  const [working, setWorking] = useState(false);

  useEffect(() => {
    setSelected(new Set(kegs.map((k) => k.kegCode)));
    const dates = kegs.map((k) => k.suggestedOrderDate).filter(Boolean).sort() as string[];
    if (dates[0]) setOrderDate(dates[0]);
  }, [kegs]);

  useEffect(() => {
    if (!selected.size) {
      setWarnings([]);
      return;
    }
    let cancelled = false;
    (async () => {
      const res = await apiFetch(`${COUNTS_API}/${countId}/place-order/preview`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ kegCodes: Array.from(selected), orderDate }),
        allowHttpError: true,
      });
      const body = await readJson(res);
      if (cancelled) return;
      if (res.ok) setWarnings(body.periodWarnings || []);
    })().catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [countId, selected, orderDate]);

  const today = new Date().toLocaleDateString('en-CA', { timeZone: 'America/Chicago' });
  const differentMonth = orderDate.slice(0, 7) !== today.slice(0, 7);

  const place = async () => {
    if (!selected.size) return;
    if (
      !window.confirm(
        `Place taproom order for ${selected.size} keg${selected.size === 1 ? '' : 's'} on ${orderDate}? ` +
          `This creates a normal brewery cooler → taproom order and moves inventory.`
      )
    )
      return;
    setWorking(true);
    try {
      const res = await apiFetch(`${COUNTS_API}/${countId}/place-order`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ kegCodes: Array.from(selected), orderDate }),
        allowHttpError: true,
      });
      const body = await readJson(res);
      if (!res.ok) {
        onError(body.error || `Could not place order (${res.status})`);
        return;
      }
      const warn = (body.periodWarnings || []).join(' ');
      onDone(
        body.count,
        `Taproom order ${body.order?.orderId || ''} placed (${body.orderDate}).${warn ? ` ${warn}` : ''}`
      );
    } catch (e: any) {
      onError(e.message || 'Could not place order');
    } finally {
      setWorking(false);
    }
  };

  if (!kegs.length) return null;

  return (
    <div className="tkc-place-order" data-testid="tkc-place-order">
      <p className="tkc-muted">
        These kegs are physically here but keg tracking has them elsewhere — usually a missed taproom order.
        Place a normal order, or skip and approve the count with them listed.
      </p>
      <ul className="tkc-list">
        {kegs.map((k) => (
          <li key={k.kegCode} data-testid={`tkc-elsewhere-${k.kegCode}`} className="tkc-ns-row">
            <label className="tkc-check tkc-elsewhere-check">
              <input
                type="checkbox"
                checked={selected.has(k.kegCode)}
                disabled={busy || working}
                onChange={(e) => {
                  const next = new Set(selected);
                  if (e.target.checked) next.add(k.kegCode);
                  else next.delete(k.kegCode);
                  setSelected(next);
                }}
              />
              <span>
                <strong>{k.kegCode}</strong> · {k.productName || 'No product'} · {sizeLabel(k.packageType)}
                <div className="tkc-muted">
                  Recorded at {k.wasAt || 'no location'} ({k.status || '—'})
                  {k.atBreweryCooler ? ' · brewery cooler' : ''}
                </div>
              </span>
            </label>
          </li>
        ))}
      </ul>
      <div className="tkc-order-date-row">
        <label htmlFor="tkc-order-date">
          Order date
          <input
            id="tkc-order-date"
            type="date"
            value={orderDate}
            max={today}
            disabled={busy || working || !selected.size}
            onChange={(e) => setOrderDate(e.target.value)}
            data-testid="tkc-order-date"
          />
        </label>
      </div>
      {(differentMonth || warnings.length > 0) && (
        <div className="tkc-notice tkc-notice--warn" role="status" data-testid="tkc-order-warnings">
          {differentMonth && (
            <div>
              Order date {orderDate} is in a different month than today ({today}). That month&apos;s end-of-month
              inventory and ABC report will include this move.
            </div>
          )}
          {warnings.map((w) => (
            <div key={w}>{w}</div>
          ))}
        </div>
      )}
      <button
        type="button"
        className="tkc-btn tkc-btn--go"
        data-testid="tkc-place-order-btn"
        disabled={busy || working || !selected.size}
        onClick={() => void place()}
      >
        Place taproom order now ({selected.size})
      </button>
    </div>
  );
};

/** One count: scan (draft) → review → approve; approved = printable detail. */
export const TaproomKegCountDetail: React.FC = () => {
  const { countId } = useParams<{ countId: string }>();
  const navigate = useNavigate();
  const [doc, setDoc] = useState<TaproomKegCountDoc | null>(null);
  const [step, setStep] = useState<'scan' | 'review'>('scan');
  const [notice, setNotice] = useState<Notice | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [busy, setBusy] = useState(false);
  const [showCamera, setShowCamera] = useState(false);
  const navClearance = useBottomNavClearance();
  const barStyle: React.CSSProperties | undefined = navClearance != null ? { bottom: navClearance } : undefined;
  const inputRef = useRef<HTMLInputElement>(null);
  const lastScan = useRef<{ code: string; at: number }>({ code: '', at: 0 });
  const docRef = useRef<TaproomKegCountDoc | null>(doc);
  docRef.current = doc;

  const load = useCallback(async () => {
    const res = await apiFetch(`${COUNTS_API}/${countId}`, { headers: { Accept: 'application/json' } });
    if (!res.ok) throw new Error(`Failed to load count: HTTP ${res.status}`);
    setDoc(await res.json());
  }, [countId]);

  useEffect(() => {
    load().catch((e: Error) => setError(e.message));
  }, [load]);

  const addCode = async (raw: string) => {
    if (!doc || doc.status !== 'draft') return;
    const c = normalizeKegCode(raw);
    if (!isValidKegCode(c)) {
      setNotice({ level: 'error', message: 'Valid keg tag needed (e.g., EK09503)' });
      return;
    }
    const now = Date.now();
    if (lastScan.current.code === c && now - lastScan.current.at < SCAN_COOLDOWN_MS) return;
    lastScan.current = { code: c, at: now };
    setBusy(true);
    try {
      const res = await apiFetch(`${COUNTS_API}/${doc.id}/lines`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ code: c }),
        allowHttpError: true,
      });
      const body = await readJson(res);
      if (!res.ok) {
        setNotice({ level: 'error', message: body.error || `Could not add ${c} (${res.status})` });
        return;
      }
      if (body.count) setDoc(body.count);
      setNotice(body.notice || { level: 'ok', message: `${c} counted` });
      setCode('');
    } catch (e: any) {
      setNotice({ level: 'error', message: e.message || `Could not add ${c}` });
    } finally {
      setBusy(false);
      if (!showCamera) inputRef.current?.focus();
    }
  };

  const scanFromCamera = async (c: string): Promise<KegScanAccepted> => {
    const current = docRef.current;
    if (!current || current.status !== 'draft') throw new Error('This count is not a draft');
    const res = await apiFetch(`${COUNTS_API}/${current.id}/lines`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ code: c }),
      allowHttpError: true,
    });
    const body = await readJson(res);
    if (!res.ok) throw new Error(body.error || `Could not add ${c} (${res.status})`);
    if (body.count) {
      docRef.current = body.count;
      setDoc(body.count);
    }
    const n: Notice = body.notice || { level: 'ok', message: `${c} counted` };
    setNotice(n);
    return n.level === 'ok' || n.level === 'info' ? { message: n.message } : { level: 'warn', message: n.message };
  };

  const mutate = async (url: string, init: RequestInit) => {
    setBusy(true);
    setError(null);
    try {
      const res = await apiFetch(url, {
        ...init,
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        allowHttpError: true,
      });
      const body = await readJson(res);
      if (!res.ok) {
        setError(body.error || `Request failed (${res.status})`);
        return null;
      }
      return body;
    } catch (e: any) {
      setError(e.message);
      return null;
    } finally {
      setBusy(false);
    }
  };

  const removeLine = async (lineId: number) => {
    if (!doc) return;
    const body = await mutate(`${COUNTS_API}/${doc.id}/lines/${lineId}`, { method: 'DELETE' });
    if (body) setDoc(body);
  };

  const approve = async () => {
    if (!doc) return;
    const missing = (doc.missing || doc.notScanned || []).length;
    const returned = (doc.returnedEmpty || []).length;
    const elsewhere = (doc.elsewhere || []).length;
    if (
      !window.confirm(
        `Approve ${doc.ref} for ${doc.taproom}?\n\n` +
          `This only records the count and its lists — no inventory or keg locations change.\n` +
          `Matched ${doc.totals.matched ?? doc.totals.counted ?? 0}` +
          (returned ? ` · returned-empty ${returned}` : '') +
          (elsewhere ? ` · out of place ${elsewhere}` : '') +
          (missing ? ` · missing ${missing}` : '') +
          '.'
      )
    )
      return;
    const body = await mutate(`${COUNTS_API}/${doc.id}/approve`, {
      method: 'POST',
      body: JSON.stringify({}),
    });
    if (body) {
      setDoc(body);
      setNotice({
        level: 'ok',
        message: body.alreadyApproved ? `${body.ref} was already approved` : `${body.ref} approved (lists recorded only)`,
      });
    }
  };

  const deleteDraft = async () => {
    if (!doc || !window.confirm(`Delete draft ${doc.ref}? Nothing has been changed by this draft.`)) return;
    const body = await mutate(`${COUNTS_API}/${doc.id}`, { method: 'DELETE' });
    if (body) navigate('/taproom-keg-counts');
  };

  if (!doc) {
    return (
      <div className="page-container">{error ? <div className="error">{error}</div> : <p>Loading count…</p>}</div>
    );
  }

  const approved = doc.status === 'approved';
  const scannedLines = [...doc.lines].reverse();
  const missingList = doc.missing || doc.notScanned || [];
  const returnedList =
    doc.returnedEmpty ||
    doc.lines
      .filter((l) => l.flag === 'returned_empty')
      .map((l) => ({
        kegCode: l.kegCode,
        productName: l.restoreProductName || l.effectiveProductName,
        packageType: l.packageType,
        wasAt: l.wasAt,
        returnWarning: l.returnWarning,
      }));
  const unknownLines = doc.lines.filter((l) => l.flag === 'unknown');

  const header = (
    <>
      <p className="keg-return-no-print">
        <Link to="/taproom-keg-counts">← All counts</Link> · <Link to="/keg-tracking">Keg Tracking</Link>
      </p>
      <h2 className="app-header tkc-title" style={{ color: '#EEC930', fontSize: 22 }}>
        {doc.ref} · {doc.taproom}{' '}
        <span className={`keg-return-status keg-return-status-${doc.status}`}>{doc.status}</span>
      </h2>
      {approved && (
        <p className="tkc-meta" data-testid="tkc-approved-meta">
          Approved {formatCentral(doc.approvedAt)} by {doc.approvedByEmail || '—'} · {siteFor(doc.taproom)} · lists
          only (no inventory move)
        </p>
      )}
      <NoticeBar notice={notice} />
      {error && (
        <div className="tkc-notice tkc-notice--error" role="alert" data-testid="tkc-error">
          {error}
        </div>
      )}
    </>
  );

  const tallyTable = (
    <section className="tkc-section" data-testid="tkc-review-tally">
      <h3 className="tkc-h3">Matched vs {doc.taproom} On Hand</h3>
      <p className="tkc-muted">Informational only — approve does not true-up On Hand.</p>
      <table className="tkc-table">
        <thead>
          <tr>
            <th>Product</th>
            <th>Size</th>
            <th>On Hand</th>
            <th>Matched</th>
            <th>Diff</th>
          </tr>
        </thead>
        <tbody>
          {doc.tally.map((t) => (
            <tr key={t.itemName} data-testid={`tkc-tally-row-${t.itemName}`}>
              <td>{t.productName || t.itemName}</td>
              <td>{sizeLabel(t.packageType)}</td>
              <td>{t.onHand}</td>
              <td>{t.scanned}</td>
              <td className={t.diff > 0 ? 'tkc-up' : t.diff < 0 ? 'tkc-down' : ''}>{signed(t.diff)}</td>
            </tr>
          ))}
          {doc.tally.length === 0 && (
            <tr>
              <td colSpan={5}>No matched kegs and no keg On Hand at {doc.taproom}.</td>
            </tr>
          )}
        </tbody>
        <tfoot>
          <tr>
            <td colSpan={2}>Total</td>
            <td>{doc.totals.onHand ?? 0}</td>
            <td>{doc.totals.counted ?? 0}</td>
            <td>{signed(Number(doc.totals.net || 0))}</td>
          </tr>
        </tfoot>
      </table>
    </section>
  );

  const returnedEmptySection = (
    <section className="tkc-section" data-testid="tkc-returned-empty">
      <h3 className="tkc-h3">Returned empty, still in cooler ({returnedList.length})</h3>
      <p className="tkc-muted">
        Already returned to Outdoor Racks (Empty) but still sitting in the cooler. Listed only — no changes.
      </p>
      {returnedList.length === 0 ? (
        <p className="tkc-muted">None.</p>
      ) : (
        <ul className="tkc-list">
          {returnedList.map((k) => (
            <li key={k.kegCode} data-testid={`tkc-returned-${k.kegCode}`}>
              <div>
                <strong>{k.kegCode}</strong> · {k.productName || 'No product'} · {sizeLabel(k.packageType)}
                {k.returnWarning && <div className="tkc-returned-empty-warn">{k.returnWarning}</div>}
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );

  const elsewhereSection = (
    <section className="tkc-section" data-testid="tkc-review-elsewhere">
      <h3 className="tkc-h3">Out of place — missed order? ({doc.elsewhere.length})</h3>
      {approved ? (
        doc.elsewhere.length === 0 ? (
          <p className="tkc-muted">None.</p>
        ) : (
          <ul className="tkc-list">
            {doc.elsewhere.map((k) => (
              <li key={k.kegCode}>
                <strong>{k.kegCode}</strong> · {k.productName || '—'} · was at {k.wasAt || 'no location'}
              </li>
            ))}
          </ul>
        )
      ) : (
        <PlaceOrderPanel
          countId={doc.id}
          kegs={doc.elsewhere}
          busy={busy}
          onDone={(count, message) => {
            setDoc(count);
            setNotice({ level: 'ok', message });
            setError(null);
          }}
          onError={(msg) => setError(msg)}
        />
      )}
    </section>
  );

  const missingSection = (
    <section className="tkc-section" data-testid="tkc-review-not-scanned">
      <h3 className="tkc-h3">Missing kegs ({missingList.length})</h3>
      <p className="tkc-muted">
        Tilly has these At Customer (or Filled) at {doc.taproom}, but they were not scanned. Alert only — record a
        return under Keg Returns → Taproom return if they left without one.
      </p>
      {missingList.length === 0 ? (
        <p className="tkc-muted">None. Every full keg at {doc.taproom} was scanned.</p>
      ) : (
        <ul className="tkc-list">
          {missingList.map((k) => (
            <li key={k.kegId} data-testid={`tkc-not-scanned-${k.kegCode}`} className="tkc-ns-row">
              <div>
                <strong>{k.kegCode}</strong> · {k.productName || 'No product'} · {sizeLabel(k.packageType)}
                <div className="tkc-muted">
                  {k.arrivedDate || k.lastHistoryDate || k.lastScanned
                    ? `Went to taproom ${formatDay(k.arrivedDate || k.lastHistoryDate || k.lastScanned)}`
                    : 'Arrival date unknown'}
                  {k.orderRef ? ` · ${k.orderRef}` : k.arrivedVia ? ` · ${k.arrivedVia}` : ''}
                  {k.status ? ` · ${k.status}` : ''}
                </div>
              </div>
            </li>
          ))}
        </ul>
      )}
    </section>
  );

  const unknownSection =
    unknownLines.length > 0 ? (
      <section className="tkc-section" data-testid="tkc-unknown">
        <h3 className="tkc-h3">Unknown keg codes ({unknownLines.length})</h3>
        <p className="tkc-muted">Remove these or register them in Keg Tracking before approving.</p>
        <ul className="tkc-list">
          {unknownLines.map((l) => (
            <li key={l.id} data-testid={`tkc-unknown-${l.kegCode}`}>
              <strong>{l.kegCode}</strong>
              {!approved && (
                <button type="button" onClick={() => void removeLine(l.id)} disabled={busy}>
                  Remove
                </button>
              )}
            </li>
          ))}
        </ul>
      </section>
    ) : null;

  if (approved) {
    return (
      <div className="page-container tkc-page keg-return-print" data-testid="tkc-detail" data-status={doc.status}>
        {header}
        {tallyTable}
        {returnedEmptySection}
        {elsewhereSection}
        {missingSection}
        {unknownSection}
        <section className="tkc-section">
          <h3 className="tkc-h3">Kegs scanned ({doc.lines.length})</h3>
          <p className="tkc-muted">{doc.lines.map((l) => l.kegCode).join(', ')}</p>
        </section>
        <div className="tkc-action-bar keg-return-no-print" data-testid="tkc-action-bar" style={barStyle}>
          <button type="button" className="tkc-btn" onClick={() => window.print()} data-testid="tkc-print">
            Print
          </button>
          <button type="button" className="tkc-btn tkc-btn--muted" onClick={() => navigate('/taproom-keg-counts')}>
            Back
          </button>
        </div>
      </div>
    );
  }

  if (step === 'review') {
    return (
      <div className="page-container tkc-page" data-testid="tkc-review">
        {header}
        {doc.blockers.length > 0 && (
          <div className="tkc-notice tkc-notice--warn" data-testid="tkc-blockers">
            {doc.blockers.map((b) => (
              <div key={b}>{b}</div>
            ))}
          </div>
        )}
        {tallyTable}
        {returnedEmptySection}
        {elsewhereSection}
        {missingSection}
        {unknownSection}
        <section className="tkc-section" data-testid="tkc-review-lines">
          <h3 className="tkc-h3">All scanned ({doc.lines.length})</h3>
          <ul className="tkc-list">
            {doc.lines.map((l) => (
              <li
                key={l.id}
                className={
                  l.flag === 'unknown'
                    ? 'tkc-line--warn'
                    : l.flag === 'returned_empty'
                      ? 'tkc-line--returned-empty'
                      : l.flag === 'elsewhere'
                        ? 'tkc-line--elsewhere'
                        : ''
                }
                data-testid={`tkc-review-line-${l.kegCode}`}
              >
                <div className="tkc-line-main">
                  <strong>{l.kegCode}</strong> · {sizeLabel(l.packageType)} ·{' '}
                  {l.flag === 'returned_empty'
                    ? l.restoreProductName || l.effectiveProductName || 'Returned empty'
                    : l.effectiveProductName || 'No product'}
                  {!l.flag && <span className="keg-return-flag keg-return-flag--ok">Match</span>}
                  {l.flag === 'unknown' && <span className="keg-return-flag">Unknown tag</span>}
                  {l.flag === 'elsewhere' && (
                    <span className="keg-return-flag">Out of place · {l.wasAt}</span>
                  )}
                  {l.flag === 'returned_empty' && (
                    <span className="keg-return-flag" data-testid={`tkc-returned-empty-flag-${l.kegCode}`}>
                      Returned empty
                    </span>
                  )}
                  {l.currentStatus && !l.flag && (
                    <div className="tkc-muted">{l.currentStatus}</div>
                  )}
                </div>
                {l.flag === 'unknown' && (
                  <button type="button" onClick={() => void removeLine(l.id)} disabled={busy}>
                    Remove
                  </button>
                )}
              </li>
            ))}
          </ul>
        </section>
        <div className="tkc-action-bar" data-testid="tkc-action-bar" style={barStyle}>
          <button type="button" className="tkc-btn tkc-btn--muted" onClick={() => setStep('scan')} data-testid="tkc-back-to-scan">
            Keep scanning
          </button>
          <button
            type="button"
            className="tkc-btn tkc-btn--go"
            data-testid="tkc-approve"
            disabled={busy || doc.blockers.length > 0}
            onClick={() => void approve()}
          >
            Approve count
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="page-container tkc-page" data-testid="tkc-scan">
      {header}
      <p className="tkc-muted tkc-autosave-hint" data-testid="tkc-autosave-hint">
        Scans save automatically. Safe to leave and resume later.
      </p>
      <TallyChips lines={doc.lines} />
      <div className="tkc-scan-row">
        <input
          ref={inputRef}
          type="text"
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              void addCode(code);
            }
          }}
          placeholder="Scan or type keg tag"
          data-testid="tkc-scan-input"
          disabled={busy}
          autoComplete="off"
          autoCapitalize="characters"
          aria-label="Keg tag"
        />
        <button type="button" className="tkc-btn" data-testid="tkc-add" disabled={busy} onClick={() => void addCode(code)}>
          Add
        </button>
        <button
          type="button"
          className="tkc-btn tkc-btn--dark"
          data-testid="tkc-camera"
          onClick={() => {
            primeScanAudio();
            setShowCamera(true);
          }}
        >
          Camera
        </button>
      </div>
      <ul className="tkc-list" data-testid="tkc-scanned">
        {scannedLines.map((l) => (
          <li key={l.id} className={l.flag ? `tkc-line--${l.flag}` : ''} data-testid={`tkc-line-${l.kegCode}`}>
            <div>
              <strong>{l.kegCode}</strong> ·{' '}
              {l.flag === 'returned_empty'
                ? l.restoreProductName || 'Returned empty'
                : l.effectiveProductName || 'No product'}{' '}
              · {sizeLabel(l.packageType)}
              {l.flag === 'unknown' && <span className="keg-return-flag">Unknown tag</span>}
              {l.flag === 'elsewhere' && <span className="keg-return-flag">Out of place · {l.wasAt}</span>}
              {l.flag === 'returned_empty' && <span className="keg-return-flag">Returned empty</span>}
              {l.flag === 'returned_empty' && l.returnWarning && (
                <div className="tkc-returned-empty-warn">{l.returnWarning}</div>
              )}
            </div>
            <button type="button" onClick={() => void removeLine(l.id)} disabled={busy} aria-label={`Remove ${l.kegCode}`}>
              Remove
            </button>
          </li>
        ))}
        {doc.lines.length === 0 && <li className="tkc-muted">Scan the first keg to start the count.</li>}
      </ul>
      <div className="tkc-action-bar" data-testid="tkc-action-bar" style={barStyle}>
        <button type="button" className="tkc-btn tkc-btn--danger" onClick={() => void deleteDraft()} disabled={busy}>
          Delete draft
        </button>
        <button
          type="button"
          className="tkc-btn tkc-btn--go"
          data-testid="tkc-review-button"
          disabled={busy || doc.lines.length === 0}
          onClick={() => {
            setNotice(null);
            setStep('review');
          }}
        >
          Review ({doc.lines.length})
        </button>
      </div>

      {showCamera && (
        <KegScanOverlay
          title={`Scan kegs at ${doc.taproom}`}
          subtitle={doc.ref}
          codes={doc.lines.map((l) => l.kegCode)}
          extra={<TallyChips lines={doc.lines} />}
          describe={(c) => {
            const want = normalizeKegCode(c);
            const l = doc.lines.find((x) => normalizeKegCode(x.kegCode) === want);
            if (!l) return null;
            const flag =
              l.flag === 'unknown'
                ? ' · Unknown tag'
                : l.flag === 'returned_empty'
                  ? ' · Returned empty'
                  : l.flag === 'elsewhere'
                    ? ` · out of place (${l.wasAt})`
                    : ' · Match';
            const product =
              l.flag === 'returned_empty'
                ? l.restoreProductName || 'Returned empty'
                : l.effectiveProductName || 'No product';
            return `${product} · ${sizeLabel(l.packageType)}${flag}`;
          }}
          onScan={scanFromCamera}
          onRemove={async (c) => {
            const want = normalizeKegCode(c);
            const l = (docRef.current?.lines || []).find(
              (x) => normalizeKegCode(x.kegCode) === want
            );
            if (l) await removeLine(l.id);
          }}
          doneLabel="Done scanning"
          onDone={() => setShowCamera(false)}
        />
      )}
    </div>
  );
};

export default TaproomKegCountList;
